"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import {
  Award,
  ArrowLeft,
  TrendingUp,
  Users,
  MessageSquare,
  ShoppingBag,
  Search,
  Quote,
  Filter,
  Sparkles,
  BookOpen,
  Loader2,
} from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import {
  ShimmerBar,
  EmptyState,
  ErrorState,
} from "@/components/site/StatePrimitives";
import { useInfiniteList } from "@/hooks/useInfiniteList";
import { seoMeta, seoLinks, jsonLd, breadcrumbLd, itemListLd, organizationLd } from "@/lib/seo/seo";

const PAGE_TITLE = "نمونه‌کارها و داستان‌های موفقیت برندهای ایرانی";
const PAGE_DESC =
  "ببین چطور بیش از ۵٬۰۰۰ کسب‌وکار ایرانی با ترندی اسمارت زمان پاسخ‌گویی را تا ۹۲٪ کم کردند، فروش را تا ۳ برابر افزایش دادند و تیم پشتیبانی را خودکار کردند.";

type Industry = "all" | "fashion" | "beauty" | "food" | "digital" | "edu";

type Study = {
  id: string;
  brand: string;
  industry: Exclude<Industry, "all">;
  industryLabel: string;
  headline: string;
  summary: string;
  metrics: { label: string; value: string; tone: "primary" | "secondary" | "success" }[];
  quote: string;
  author: string;
  authorRole: string;
  initials: string;
  color: string;
  featured?: boolean;
};

const ALL: Study[] = [
  {
    id: "modaye",
    brand: "مدآی",
    industry: "fashion",
    industryLabel: "مد و پوشاک",
    headline: "از پاسخ دستی به ۸۰٪ خودکارسازی DM",
    summary:
      "مدآی با اتوماسیون پاسخ به DM اینستاگرام و کامنت، زمان پاسخ‌گویی را از ۴ ساعت به کمتر از ۲ دقیقه رساند.",
    metrics: [
      { label: "نرخ تبدیل DM", value: "۳.۲×", tone: "primary" },
      { label: "زمان پاسخ", value: "−۹۲٪", tone: "success" },
      { label: "فروش ماهانه", value: "+۱۸۰٪", tone: "secondary" },
    ],
    quote:
      "ما دیگر نگران از دست دادن مشتری در ساعات شلوغ نیستیم. ترندی اسمارت مثل یک تیم پشتیبانی همیشه‌بیدار است.",
    author: "نگار صفری",
    authorRole: "بنیان‌گذار مدآی",
    initials: "ن.ص",
    color: "from-primary-main to-primary-300",
    featured: true,
  },
  {
    id: "rouna",
    brand: "روناآرایش",
    industry: "beauty",
    industryLabel: "زیبایی و آرایش",
    headline: "افزایش ۲.۴ برابری فروش بعد از یک کمپین استوری",
    summary: "اتوماسیون پاسخ به منشن استوری و کامنت پست‌ها همراه با کوپن‌های هوشمند.",
    metrics: [
      { label: "نرخ تعامل", value: "+۲۲۰٪", tone: "primary" },
      { label: "AOV", value: "+۴۵٪", tone: "secondary" },
      { label: "ROAS", value: "۵.۶×", tone: "success" },
    ],
    quote:
      "اولین برندی هستیم که تونستیم در یک هفته بدون افزایش بودجه تبلیغات، فروش را دو برابر کنیم.",
    author: "آرمیتا حق‌گو",
    authorRole: "مدیر دیجیتال مارکتینگ",
    initials: "آ.ح",
    color: "from-secondary-main to-secondary-300",
  },
  {
    id: "kafeman",
    brand: "کافه‌من",
    industry: "food",
    industryLabel: "رستوران و کافه",
    headline: "ثبت سفارش از واتساپ، بدون اپ، بدون اصطکاک",
    summary: "ثبت سفارش، رهگیری و وفاداری مشتری در یک گفت‌وگوی واتساپی.",
    metrics: [
      { label: "سفارش روزانه", value: "+۹۰٪", tone: "primary" },
      { label: "بازگشت مشتری", value: "+۶۸٪", tone: "secondary" },
      { label: "هزینه پشتیبانی", value: "−۵۰٪", tone: "success" },
    ],
    quote:
      "مشتری‌های ما اپ نمی‌خوان نصب کنن. واتساپ همیشه تو دستشونه و ما همون‌جا کار رو تموم می‌کنیم.",
    author: "حامد رحیمی",
    authorRole: "مالک کافه‌من",
    initials: "ح.ر",
    color: "from-teal to-light-blue",
  },
  {
    id: "pixelup",
    brand: "پیکسل‌آپ",
    industry: "digital",
    industryLabel: "کالای دیجیتال",
    headline: "صرفه‌جویی ۲۰ ساعت در هفته با تیکت هوشمند",
    summary: "Trendi هوشمند ۷۵٪ تیکت‌های پشتیبانی را به‌تنهایی پاسخ می‌دهد.",
    metrics: [
      { label: "تیکت خودکار", value: "۷۵٪", tone: "primary" },
      { label: "CSAT", value: "۴.۸/۵", tone: "success" },
      { label: "زمان حل", value: "−۶۰٪", tone: "secondary" },
    ],
    quote: "تیم پشتیبانی ما حالا روی مسائل پیچیده تمرکز می‌کنه، نه تکرارهای روزمره.",
    author: "علیرضا نوری",
    authorRole: "مدیر عملیات",
    initials: "ع.ن",
    color: "from-purple to-blue",
    featured: true,
  },
  {
    id: "honaracademy",
    brand: "آکادمی هنر",
    industry: "edu",
    industryLabel: "آموزش آنلاین",
    headline: "ثبت‌نام دوره از اینستاگرام در ۹۰ ثانیه",
    summary: "فلوی هوشمند پاسخ به کلیدواژه‌ها همراه با فاکتور و پرداخت در DM.",
    metrics: [
      { label: "نرخ ثبت‌نام", value: "+۱۴۰٪", tone: "primary" },
      { label: "Lead به مشتری", value: "۳۸٪", tone: "success" },
      { label: "هزینه جذب", value: "−۴۰٪", tone: "secondary" },
    ],
    quote: "از نقطه‌ای که کاربر می‌نویسه «هزینه؟» تا پرداخت، ۹۰ ثانیه طول می‌کشه. این جادوست.",
    author: "زهرا کریمی",
    authorRole: "مدیر آکادمی",
    initials: "ز.ک",
    color: "from-pink to-secondary-main",
  },
  {
    id: "freshmart",
    brand: "فرش‌مارت",
    industry: "food",
    industryLabel: "خرده‌فروشی",
    headline: "اتصال SMS + اینستاگرام = حفظ مشتری",
    summary: "کمپین‌های پیامکی بازگشتی هم‌زمان با اتوماسیون اینستاگرام.",
    metrics: [
      { label: "Retention 30d", value: "+۵۵٪", tone: "primary" },
      { label: "LTV", value: "+۸۰٪", tone: "secondary" },
      { label: "تخفیف هدررفته", value: "−۳۵٪", tone: "success" },
    ],
    quote: "اولین بار است که می‌فهمیم کدام مشتری از کدام کانال آمده و چقدر برای ما می‌ارزد.",
    author: "محمد علوی",
    authorRole: "مدیر CRM",
    initials: "م.ع",
    color: "from-primary-main to-secondary-main",
  },
  {
    id: "noorgallery",
    brand: "گالری نور",
    industry: "fashion",
    industryLabel: "گالری مد",
    headline: "از پاپ‌آپ فیزیکی تا فروش روزانه آنلاین",
    summary: "اتوماسیون پیام‌های پاپ‌آپ تبدیل به مشتری همیشگی.",
    metrics: [
      { label: "Lead روزانه", value: "+۲۰۰٪", tone: "primary" },
      { label: "نرخ خرید مجدد", value: "۴۲٪", tone: "secondary" },
      { label: "هزینه پاپ‌آپ", value: "−۶۰٪", tone: "success" },
    ],
    quote: "به جای اجاره ماهانه، حالا یک کمپین یک‌روزه رو تبدیل به فروش ۳ ماهه می‌کنیم.",
    author: "سارا نوری",
    authorRole: "بنیان‌گذار",
    initials: "س.ن",
    color: "from-blue to-pink",
  },
  {
    id: "techtools",
    brand: "تک‌تولز",
    industry: "digital",
    industryLabel: "ابزار دیجیتال",
    headline: "تبدیل پشتیبانی به موتور فروش",
    summary: "با تشخیص intent در تیکت‌ها، فرصت‌های upsell را خودکار شناسایی می‌کنیم.",
    metrics: [
      { label: "Upsell نرخ", value: "۲۸٪", tone: "primary" },
      { label: "زمان حل", value: "−۵۵٪", tone: "success" },
      { label: "ARR", value: "+۹۰٪", tone: "secondary" },
    ],
    quote: "تیم پشتیبانی الان بزرگ‌ترین چنل فروش ماست.",
    author: "بهنام شریفی",
    authorRole: "VP Customer",
    initials: "ب.ش",
    color: "from-teal to-purple",
  },
  {
    id: "honeylab",
    brand: "هانی‌لب",
    industry: "beauty",
    industryLabel: "اسکین‌کر",
    headline: "Quiz هوشمند پوست در DM",
    summary: "کاربر به ۵ سؤال پاسخ می‌دهد و محصول اختصاصی پیشنهاد می‌شود.",
    metrics: [
      { label: "Quiz تکمیلی", value: "۸۲٪", tone: "primary" },
      { label: "Conversion", value: "۳۴٪", tone: "secondary" },
      { label: "Refund", value: "−۷۰٪", tone: "success" },
    ],
    quote: "Quiz به ما اجازه داد بدون مشاور حضوری، تجربه شخصی‌سازی شده ارائه کنیم.",
    author: "نگین آقایی",
    authorRole: "Founder",
    initials: "ن.آ",
    color: "from-pink to-blue",
  },
  {
    id: "kidly",
    brand: "کیدلی",
    industry: "edu",
    industryLabel: "آموزش کودک",
    headline: "ارسال محتوای روزانه به والدین در WhatsApp",
    summary: "ارسال هوشمند محتوای متناسب با سن کودک، با بازخورد لحظه‌ای.",
    metrics: [
      { label: "Open rate", value: "۹۴٪", tone: "primary" },
      { label: "Subscription", value: "+۱۲۰٪", tone: "secondary" },
      { label: "Churn", value: "−۴۵٪", tone: "success" },
    ],
    quote: "WhatsApp بستری شد که والدین تشنه‌ی محتوا اون رو هر روز چک می‌کنن.",
    author: "ریحانه صدر",
    authorRole: "Product",
    initials: "ر.ص",
    color: "from-light-blue to-teal",
  },
  {
    id: "saffronco",
    brand: "زعفران‌کو",
    industry: "food",
    industryLabel: "صادرات",
    headline: "پشتیبانی چندزبانه از ۱۲ کشور",
    summary: "Trendi AI پیام مشتری را به زبان مادری پاسخ می‌دهد.",
    metrics: [
      { label: "زبان فعال", value: "۸", tone: "primary" },
      { label: "B2B Lead", value: "+۲۰۰٪", tone: "secondary" },
      { label: "زمان پاسخ شب", value: "−۹۸٪", tone: "success" },
    ],
    quote: "ساعت ۳ بامداد یک خریدار کانادایی پاسخ گرفت و سفارش ۲۰ هزار دلاری ثبت شد.",
    author: "محمدرضا فلاح",
    authorRole: "Export Manager",
    initials: "م.ف",
    color: "from-secondary-main to-primary-main",
  },
  {
    id: "bookhive",
    brand: "بوک‌هایو",
    industry: "digital",
    industryLabel: "کتاب آنلاین",
    headline: "از کامنت اینستا تا خرید کتاب در ۳ کلیک",
    summary: "تشخیص ISBN در کامنت و ارسال لینک پرداخت اختصاصی.",
    metrics: [
      { label: "CTR", value: "۶۲٪", tone: "primary" },
      { label: "AOV", value: "+۳۸٪", tone: "secondary" },
      { label: "ROAS", value: "۸.۲×", tone: "success" },
    ],
    quote: "کاربر دیگه از اینستا خارج نمی‌شه، خرید رو همون‌جا تمام می‌کنه.",
    author: "آرش کیانی",
    authorRole: "Growth",
    initials: "آ.ک",
    color: "from-purple to-secondary-main",
  },
];

const INDUSTRIES: { id: Industry; label: string }[] = [
  { id: "all", label: "همه صنایع" },
  { id: "fashion", label: "مد" },
  { id: "beauty", label: "زیبایی" },
  { id: "food", label: "غذا و کافه" },
  { id: "digital", label: "دیجیتال" },
  { id: "edu", label: "آموزش" },
];

type Scenario =
  "loaded" | "loading" | "empty" | "error" | "single" | "featured-only" | "no-results";

export default function CaseStudiesPage() {
  const [scenario, setScenario] = useState<Scenario>("loading");
  const [industry, setIndustry] = useState<Industry>("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (scenario !== "loading") return;
    const t = setTimeout(() => setScenario("loaded"), 1200);
    return () => clearTimeout(t);
  }, [scenario]);

  const items = useMemo(() => {
    if (scenario === "empty") return [];
    if (scenario === "single") return ALL.slice(0, 1);
    if (scenario === "featured-only") return ALL.filter((s) => s.featured);
    if (scenario === "no-results") return [];
    let list = ALL;
    if (industry !== "all") list = list.filter((s) => s.industry === industry);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (s) => s.brand.toLowerCase().includes(q) || s.headline.toLowerCase().includes(q),
      );
    }
    return list;
  }, [scenario, industry, query]);

  return (
    <SiteShell>
      <Hero />

      <section className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-border bg-card/60 p-3 backdrop-blur-sm sm:flex-row sm:items-center">
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
            <Search size={15} className="text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="جست‌وجوی برند یا داستان..."
              className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
            <Filter size={14} className="shrink-0 text-muted-foreground" />
            {INDUSTRIES.map((i) => (
              <button
                key={i.id}
                onClick={() => setIndustry(i.id)}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-[11.5px] font-black transition-colors ${
                  industry === i.id
                    ? "border-primary-main bg-primary-main/15 text-primary-main"
                    : "border-border bg-background text-muted-foreground hover:border-primary-main/40"
                }`}
              >
                {i.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-16 sm:px-6 sm:pb-24">
        {scenario === "loading" ? (
          <CardsSkeleton />
        ) : scenario === "error" ? (
          <ErrorState onRetry={() => setScenario("loading")} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title={scenario === "no-results" ? "نتیجه‌ای پیدا نشد" : "داستان موفقیتی هنوز ثبت نشده"}
            desc={
              scenario === "no-results"
                ? "فیلتر یا کلیدواژه را تغییر بده."
                : "به‌زودی اولین داستان‌های مشتریان منتشر می‌شود."
            }
            cta={
              <button
                onClick={() => {
                  setIndustry("all");
                  setQuery("");
                  setScenario("loaded");
                }}
                className="rounded-xl bg-primary-main px-5 py-2.5 text-[12.5px] font-black text-white hover:bg-primary-400"
              >
                پاکسازی فیلترها
              </button>
            }
          />
        ) : (
          <InfiniteGrid items={items} />
        )}
      </section>

      <CTA />
    </SiteShell>
  );
}

function Hero() {
  return (
    <section className="mx-auto max-w-7xl px-5 pt-16 pb-10 sm:px-6 sm:pt-24">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
          <Award size={14} className="text-secondary-main" />
          <span className="text-[12px] font-bold text-muted-foreground">
            داستان‌های واقعی، اعداد واقعی
          </span>
        </div>
        <h1 className="mx-auto mb-5 max-w-3xl text-4xl font-black leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">
          چطور با ترندی اسمارت
          <span className="block brand-gradient-text">رشد کردند؟</span>
        </h1>
        <p className="mx-auto max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-[15px]">
          از فروشگاه‌های نوپا تا برندهای بزرگ — مطالعات موردی واقعی با اعداد قابل بررسی و درس‌های
          قابل تکرار.
        </p>

        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { v: "۵٬۰۰۰+", l: "کسب‌وکار فعال", icon: ShoppingBag },
            { v: "۲.۸×", l: "میانگین رشد فروش", icon: TrendingUp },
            { v: "۹۲٪", l: "کاهش زمان پاسخ", icon: MessageSquare },
            { v: "+۱.۶M", l: "گفت‌وگوی هوشمند", icon: Users },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }}
                className="rounded-2xl border border-border bg-card/70 p-4 backdrop-blur-sm"
              >
                <Icon size={16} className="mx-auto mb-1.5 text-primary-main" />
                <div className="text-[18px] font-black text-foreground">{s.v}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">{s.l}</div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
}

function StudyCard({ study, index }: { study: Study; index: number }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.04 }}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-border bg-card transition-all hover:-translate-y-1 hover:border-primary-main/40 hover:shadow-2xl hover:shadow-primary-main/10"
    >
      {study.featured && (
        <div className="absolute left-3 top-3 z-10 rounded-full bg-secondary-main/15 px-2.5 py-1 text-[10px] font-black text-secondary-main">
          ★ منتخب
        </div>
      )}
      <div className={`relative h-36 overflow-hidden bg-gradient-to-br ${study.color}`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.25),transparent_60%)]" />
        <div className="absolute bottom-3 right-4 left-4 flex items-end justify-between text-white">
          <div>
            <div className="text-[10px] font-bold opacity-80">{study.industryLabel}</div>
            <div className="mt-0.5 text-2xl font-black">{study.brand}</div>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 text-[14px] font-black backdrop-blur-md">
            {study.initials}
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="mb-2 text-[15px] font-black leading-snug text-foreground">
          {study.headline}
        </h3>
        <p className="mb-4 text-[12.5px] leading-relaxed text-muted-foreground">{study.summary}</p>

        <div className="mb-4 grid grid-cols-3 gap-2">
          {study.metrics.map((m) => {
            const tone =
              m.tone === "primary"
                ? "text-primary-main"
                : m.tone === "secondary"
                  ? "text-secondary-main"
                  : "text-success";
            return (
              <div
                key={m.label}
                className="rounded-xl border border-border bg-background p-2 text-center"
              >
                <div className={`text-[14px] font-black ${tone}`}>{m.value}</div>
                <div className="mt-0.5 text-[9.5px] text-muted-foreground">{m.label}</div>
              </div>
            );
          })}
        </div>

        <div className="mt-auto rounded-2xl border border-dashed border-border bg-background/60 p-3">
          <Quote size={12} className="mb-1.5 text-secondary-main" />
          <p className="text-[12px] leading-relaxed text-foreground/90">«{study.quote}»</p>
          <div className="mt-2 text-[10.5px] text-muted-foreground">
            <b className="text-foreground">{study.author}</b> — {study.authorRole}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function CardsSkeleton() {
  return (
    <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-3xl border border-border bg-card">
          <ShimmerBar className="h-36 rounded-none" />
          <div className="space-y-3 p-5">
            <ShimmerBar className="h-4 w-3/4" />
            <ShimmerBar className="h-3 w-full" />
            <ShimmerBar className="h-3 w-5/6" />
            <div className="grid grid-cols-3 gap-2 pt-2">
              <ShimmerBar className="h-12" />
              <ShimmerBar className="h-12" />
              <ShimmerBar className="h-12" />
            </div>
            <ShimmerBar className="h-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

function StudyCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-border bg-card">
      <ShimmerBar className="h-36 rounded-none" />
      <div className="space-y-3 p-5">
        <ShimmerBar className="h-4 w-3/4" />
        <ShimmerBar className="h-3 w-full" />
        <ShimmerBar className="h-3 w-5/6" />
        <div className="grid grid-cols-3 gap-2 pt-2">
          <ShimmerBar className="h-12" />
          <ShimmerBar className="h-12" />
          <ShimmerBar className="h-12" />
        </div>
        <ShimmerBar className="h-16" />
      </div>
    </div>
  );
}

function InfiniteGrid({ items }: { items: Study[] }) {
  const { visible, hasMore, loadingMore, sentinelRef, total, visibleCount } =
    useInfiniteList<Study>({ items, pageSize: 6, loadDelay: 700, resetKey: items });
  return (
    <>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {visible.map((s, i) => (
          <StudyCard key={s.id} study={s} index={i} />
        ))}
        {loadingMore &&
          hasMore &&
          Array.from({ length: 3 }).map((_, k) => <StudyCardSkeleton key={`sk-${k}`} />)}
      </div>
      <div ref={sentinelRef} className="h-10" aria-hidden />
      <div className="mt-6 flex items-center justify-center">
        {hasMore ? (
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-[11.5px] font-bold text-muted-foreground">
            <Loader2 size={12} className="animate-spin text-primary-main" />
            در حال بارگذاری بیشتر... ({visibleCount} از {total})
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-[11.5px] font-bold text-muted-foreground">
            <Sparkles size={12} className="text-secondary-main" />
            تمام {total} داستان نمایش داده شد
          </div>
        )}
      </div>
    </>
  );
}

function CTA() {
  return (
    <section className="mx-auto max-w-5xl px-5 pb-24 sm:px-6">
      <div className="rounded-3xl border border-primary-main/30 bg-gradient-to-br from-primary-main/10 via-card to-secondary-main/10 p-8 text-center sm:p-12">
        <Sparkles size={20} className="mx-auto mb-3 text-secondary-main" />
        <h2 className="mb-3 text-2xl font-black text-foreground sm:text-3xl">
          داستان بعدی می‌تونه مال تو باشه
        </h2>
        <p className="mx-auto mb-6 max-w-md text-[13px] text-muted-foreground">
          ۱۴ روز رایگان امتحان کن، اگر نتیجه نگرفتی، بدون سؤال بازگشت وجه.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            href="/login"
            className="inline-flex items-center gap-2 rounded-xl bg-primary-main px-6 py-3 text-[13px] font-black text-white hover:bg-primary-400"
          >
            شروع رایگان <ArrowLeft size={14} />
          </Link>
          <Link
            href="/customers"
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-6 py-3 text-[13px] font-black text-foreground hover:bg-muted"
          >
            مشتریان ما
          </Link>
        </div>
      </div>
    </section>
  );
}
